import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import styles from './shareModal.module.scss';
import { t } from 'i18next';

// Types
interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  url?: string;
  title?: string;
  description?: string;
  imageUrl?: string; // Add imageUrl prop
  hashtags?: string[];
  via?: string;
}

interface SocialPlatform {
  name: string;
  icon: React.ReactNode;
  url: string;
  className: string;
  customContent?: boolean;
}

const XIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const PinterestIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.024-.105-.949-.199-2.403.041-3.439.219-.937 1.219-5.160 1.219-5.160s-.219-.438-.219-1.085c0-1.016.589-1.775 1.323-1.775.623 0 .926.466.926 1.026 0 .625-.397 1.560-.603 2.425-.171.725.363 1.315 1.077 1.315 1.291 0 2.188-1.681 2.188-3.652 0-1.503-.980-2.629-2.696-2.629-1.890 0-3.065 1.382-3.065 2.928 0 .535.159 .913.469 1.205.130.154.149.219.101.438-.035.154-.117.466-.149.595-.05.219-.219.267-.438.161-1.249-.466-1.844-1.724-1.844-3.137 0-2.319 2.073-5.081 6.19-5.081 3.307 0 5.187 2.370 5.187 4.914 0 3.257-1.811 5.691-4.490 5.691-.905 0-1.751-.485-2.042-1.077 0 0-.466 1.845-.568 2.207-.219.845-.681 1.567-1.065 2.150.876.267 1.804.404 2.770.404 6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001.017 0z"/>
  </svg>
);

const WhatsAppIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.064 3.488z"/>
  </svg>
);

const ShareModal: React.FC<ShareModalProps> = ({ 
  open, 
  onClose, 
  url,
  title = "Check out this amazing content!",
  description = "I found this interesting and wanted to share it with you.",
  imageUrl,
  hashtags = [],
  via = ""
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const currentUrl: string = url || window.location.href;

  // Function to update meta tags dynamically
  const updateMetaTags = (title: string, description: string, url: string, imageUrl?: string) => {
    // Remove existing meta tags
    const existingTags = document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]');
    existingTags.forEach(tag => tag.remove());

    // Create new meta tags
    const metaTags = [
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: url },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];

    if (imageUrl) {
      metaTags.push(
        { property: 'og:image', content: imageUrl },
        { name: 'twitter:image', content: imageUrl }
      );
    }

    // Add meta tags to head
    metaTags.forEach(tagData => {
      const meta = document.createElement('meta');
      if (tagData.property) {
        meta.setAttribute('property', tagData.property);
      }
      if (tagData.name) {
        meta.setAttribute('name', tagData.name);
      }
      meta.setAttribute('content', tagData.content);
      document.head.appendChild(meta);
    });
  };

  // Update meta tags when modal opens
  useEffect(() => {
    if (open) {
      updateMetaTags(title, description, currentUrl, imageUrl);
    }
  }, [open, title, description, currentUrl, imageUrl]);

  // Create formatted content for each platform
  const formatContentForPlatform = (platform: string): string => {
    const hashtagString = hashtags.length > 0 ? ` ${hashtags.map(tag => `#${tag}`).join(' ')}` : '';
    
    switch (platform) {
      case 'facebook':
        return `${title}\n\n${description}\n\n${currentUrl}`;
      case 'twitter':
        const viaText = via ? ` via @${via}` : '';
        const twitterText = `${title}\n\n${description}\n\n${currentUrl}${hashtagString}${viaText}`;
        return twitterText.length > 280 ? `${title}\n\n${currentUrl}${hashtagString}` : twitterText;
      case 'linkedin':
        return `${title}\n\n${description}\n\n${currentUrl}`;
      case 'whatsapp':
        return `${title}\n\n${description}\n\n${currentUrl}`;
      case 'pinterest':  
        return `${title}\n\n${description}${hashtagString}`;
      default:
        return `${title}\n\n${currentUrl}`;
    }
  };

  const handleCopyUrl = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setSnackbarOpen(true);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSnackbarOpen(true);
    }
  }

  const socialMediaPlatforms: SocialPlatform[] = [
    {
      name: 'Facebook',
      icon: <FacebookIcon />,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
      className: 'facebook',
      customContent: true
    },
    {
      name: 'X (Twitter)',
      icon: <XIcon />,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(formatContentForPlatform('twitter'))}`,
      className: 'twitter',
      customContent: true
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon />,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(description)}`,
      className: 'linkedin',
      customContent: true
    },
    {
      name: 'Pinterest',
      icon: <PinterestIcon />,
      url: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(currentUrl)}&description=${encodeURIComponent(formatContentForPlatform('pinterest'))}${imageUrl ? `&media=${encodeURIComponent(imageUrl)}` : ''}`,
      className: 'pinterest',
      customContent: true
    },
    {
      name: 'WhatsApp',
      icon: <WhatsAppIcon />,
      url: `https://wa.me/?text=${encodeURIComponent(formatContentForPlatform('whatsapp'))}`,
      className: 'whatsapp',
      customContent: true
    }
  ];

  const handleSocialShare = (platform: SocialPlatform): void => {
    if (platform.customContent) {
      window.open(platform.url, '_blank', 'width=600,height=400');
    } else {
      const content = formatContentForPlatform(platform.className);
      
      navigator.clipboard.writeText(content).then(() => {
        setSnackbarOpen(true);
        setTimeout(() => {
          window.open(platform.url, '_blank');
        }, 500);
      }).catch(() => {
        window.open(platform.url, '_blank');
      });
    }
  };

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event, 
    reason?: string
  ): void => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        className={styles['share-modal']}
        PaperProps={{
          className: styles['share-modal__paper']
        }}
      >
        <DialogTitle className={styles['share-modal__header']}>
          <Typography 
            variant="h5" 
            component="h2" 
            className={styles['share-modal__title']}
          >
            {t('share')}
          </Typography>
          <IconButton
            onClick={onClose}
            className={styles['share-modal__close-btn']}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles['share-modal__content']}>
          <div className={styles['share-modal__url-section']}>
            <Typography 
              variant="subtitle1" 
              className={styles['share-modal__section-title']}
            >
              {t('Post Link')}
            </Typography>
            <TextField
              fullWidth
              value={currentUrl}
              variant="outlined"
              className={styles['share-modal__url-input']}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleCopyUrl}
                      edge="end"
                      className={styles['share-modal__copy-btn']}
                      aria-label="copy url"
                    >
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <Box className={styles['share-modal__divider-section']}>
            <Divider className={styles['share-modal__divider']} />
            <Typography className={styles['share-modal__divider-text']}>
              {t('Or')}
            </Typography>
            <Divider className={styles['share-modal__divider']} />
          </Box>

          <div className={styles['share-modal__social-buttons']}>
            {socialMediaPlatforms.map((platform: SocialPlatform) => (
              <Button
                key={platform.name}
                variant="contained"
                fullWidth
                startIcon={platform.icon}
                onClick={() => handleSocialShare(platform)}
                className={`${styles['share-modal__social-btn']} ${styles[`share-modal__social-btn--${platform.className}`]}`}
              >
                {platform.name}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          {t('URL copied to clipboard!')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareModal;